import { FunctionalityConfig } from '@/app/context/ConfigurableFunctionalityContext'
import { ID, Model } from '../model'
import { DocumentRendererProps } from '@keystone-6/document-renderer'
import { UtilitiesQuery } from '@/generated/graphql'
import { SurveyResponse } from '@/types'
import { Utility } from '@/types'
import { DeviceLimits } from '../programs-new/programs-new'

type GraphQLObject<T> = T & {
  __typename?: string
}

type RichTextDocument = DocumentRendererProps['document']

type HTML = string

export type ViewConfigText =
  | string
  | GraphQLObject<{
      text?: string
      color?: string
    }>

export type ViewConfigButton = GraphQLObject<{
  backgroundColor?: string
  color?: string
  text?: string
}>

export type ViewConfigModal = GraphQLObject<{
  title?: string
  description?: HTML | string
  primaryButton?: ViewConfigButton
}>

export type EnrollConfirmationModal = ViewConfigModal & {
  tosDocument?: RichTextDocument
}

export type OnboardingViewConfig = UtilityProgramViewConfig['onboarding']

export type SchedulerViewConfig = UtilityProgramViewConfig['scheduler']

export type LearnMoreViewConfig = UtilityProgramViewConfig['learnMore']

export type AppFunctionalityConfig = {
  charging?: {
    schedule?: { [key: string]: FunctionalityConfig }
    calendar?: { [key: string]: FunctionalityConfig }
  }
  onboarding?: { [key: string]: FunctionalityConfig }
}

export type ReferralConfig = UtilityProgramViewConfig['referral']

export type FaqConfig = UtilityProgramViewConfig['faq']

export type ChargeForecastConfig = UtilityProgramViewConfig['chargeForecast']

export type UtilityProgramConnectViewConfig =
  UtilityProgramViewConfig['utilityProgramConnect']

export type EligibleDeviceEnrollmentConfig =
  UtilityProgramViewConfig['eligibleDeviceEnrollment']

export type UtilityProgramViewConfig = Exclude<
  UtilitiesQuery['resolvedGroupViewConfig'],
  null | undefined
> & { sponsorName?: string }

export type EnrollmentVerificationStatus =
  | 'PENDING'
  | 'VERIFIED'
  | 'MORE_INFO'
  | 'NEW_INFO'
  | 'INELIGIBLE'
  | 'AUTO_PENDING'
  | 'ABANDONED'
  | 'POST_ENROLL_INELIGIBLE'

export enum EnrollmentVerificationStatusColor {
  PENDING = 'text-base-orange-500',
  VERIFIED = 'text-base-green-900',
  MORE_INFO = 'text-base-red-500',
  NEW_INFO = 'text-base-purple-500',
  INELIGIBLE = 'text-base-gray-500',
  AUTO_PENDING = 'text-base-orange-500',
  ABANDONED = 'text-base-gray-500',
  POST_ENROLL_INELIGIBLE = 'text-base-red-500',
  ELIGIBLE = 'text-base-blue-500',
}

export enum EnrollmentVerificationStatusBgColor {
  PENDING = 'base-orange-100',
  VERIFIED = 'base-green-100',
  MORE_INFO = 'base-red-100',
  NEW_INFO = 'base-purple-100',
  INELIGIBLE = 'base-grey-200',
  AUTO_PENDING = 'base-orange-100',
  ABANDONED = 'base-grey-100',
  POST_ENROLL_INELIGIBLE = 'base-red-100',
  ELIGIBLE = 'base-blue-100',
}

export type Enrollment = {
  id: number
  unenrolled_at: string | null
  ineligible: boolean
  utility_program_enrollment_id: number
  group: string
  utility_program_id: string
  unenrolled_reasons: string
  unenrolled_source: string
  enrolled: boolean
}

export type VehicleEnrollment = { vehicle_id: number } & Enrollment
export type ThermostatEnrollment = { thermostat_id: number } & Enrollment
export type ChargerEnrollment = { charger_id: number } & Enrollment
export type BehavioralEnrollment = { profile_id: number } & Enrollment

export type UtilityEnrollmentWithProgram = {
  id: number
  unenrolled_at: string
  utility_program: UtilityProgram
  vehicle_enrollments: Array<VehicleEnrollment>
  thermostat_enrollments: Array<ThermostatEnrollment>
  charger_enrollments: Array<ChargerEnrollment>
  behavioral_enrollments: Array<BehavioralEnrollment>
  external_redirect_url: string
  verification_status: EnrollmentVerificationStatus
  verified_at: string
}

export type VehicleMakeModels = {
  make: string
  model: string
}

export type UtilityProgram = Model & {
  id: ID
  name: string
  total_incentives_usd?: number
  logo_url: string | null
  tos_url: string | null
  faq_url: string
  referral_points?: number
  unenrolled_survey_url: string
  utility_ids: ID[]
  utility_plans: ID[]
  external_id?: string
  /**
   * The maximum number of devices that can be enrolled in the program.
   * This will be 99999 for programs that do not have a limit.
   */
  max_device_count: number
  required_enrollment_survey_responses: SurveyResponse[]
  preferred_display_timezone?: string
  view_config_json: UtilityProgramViewConfig
  verification_status?: EnrollmentVerificationStatus
  device_eligibility?: {
    eligible_vehicle_ids: ID[]
    ineligible_vehicle_ids: ID[]
    eligible_thermostat_ids: ID[]
    ineligible_thermostat_ids: ID[]
    eligible_charger_ids: ID[]
    ineligible_charger_ids: ID[]
    eligible_energy_system_ids: string[]
    ineligible_energy_system_ids: string[]
  }
  only_include_controllable_vehicles?: boolean
  utilities: Utility[]
  utilities_api_provider_urls: Record<number, string>
  reward_device_limits?: DeviceLimits | null
  excluded_vehicle_make_models?: VehicleMakeModels[]
  active: boolean
}
